import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router'

import { PageHeader } from '../../components/PageHeader'
import { ExperimentProtocol } from '../../components/capture/ActivityChoices'
import { CaptureJournal } from '../../components/capture/CaptureJournal'
import { JobWatch } from '../../components/record/JobFlow'
import { EXPERIMENT_RUN_WORDS as W, EXPERIMENT_WORDS } from '../../components/record/phaseWords'
import { Button, Card, Notice, Pill, ProblemNotice, ProgressBar } from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'
import { NotFound } from '../NotFound'
import { submitRecordingJob, type ExperimentLink, type Job } from '../../lib/api'
import { getPlan, getRun, listActivities, postEvent, type Activity, type ExperimentEvent, type ExperimentPlan } from '../../lib/experiments/api'
import { fmtTimer, select, selectExperiment, type State } from '../../lib/capture/phase'
import { asDeviceId } from '../../lib/devices'
import { SYNTHETIC_HEADBAND_ENABLED } from '../../lib/features'
import { webBluetoothAvailable } from '../../lib/headband'
import { clearPendingJob, readPendingJob, writePendingJob } from '../../lib/pendingJob'
import { ApiError } from '../../lib/problem'
import { isTerminalJob, useJobPolling } from '../../lib/useJobPolling'
import { CaptureController, ConnectCard, FittingScreen, SendRecording, type Source } from './RecordCapture'

type Context = { plan: ExperimentPlan; activity: Activity }
type Command = { body: ExperimentEvent; key: string }
class ProtocolUnavailable extends Error {}
type Stage = 'before' | 'activity' | 'settle' | 'after' | 'report' | 'result'
const railStage: Record<string, Stage> = { awaiting_before: 'before', activity_in_progress: 'activity', settling: 'settle',
  awaiting_after: 'after', awaiting_report: 'report', complete: 'report', incomplete: 'after', cancelled: 'before' }

function sourcePreset(): Source {
  if (!SYNTHETIC_HEADBAND_ENABLED) return 'bluetooth'
  try {
    if (localStorage.getItem('brainspot:mock-headband') === 'pro') return 'synthetic-pro'
    if (localStorage.getItem('brainspot:mock-headband') === 'classic') return 'synthetic-classic'
  } catch { /* The device chooser remains available without storage. */ }
  return 'bluetooth'
}

export function RecordExperiment({ report = false }: { report?: boolean }) {
  const { runId } = useParams()
  const { account } = useAuth()
  if (!runId || !account) return null
  if (account.experiments_enabled_for_me !== true) return <NotFound />
  return <ExperimentSession key={`${account.user_id}:${runId}`} id={runId} userId={account.user_id} report={report} />
}

function ExperimentSession({ id, userId, report }: { id: string; userId: string; report: boolean }) {
  const [ctl] = useState(() => new CaptureController())
  const [state, setState] = useState<State>(ctl.state)
  const [clock, setClock] = useState(0)
  const [context, setContext] = useState<Context | null>(null)
  const contextRef = useRef<Context | null>(null)
  const [source, setSource] = useState<Source>(sourcePreset)
  const [failure, setFailure] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const [savingStop, setSavingStop] = useState(false)
  const [retrying, setRetrying] = useState(false)
  const [orphanJob, setOrphanJob] = useState<string | null>(() => {
    const pointer = readPendingJob(userId)
    return pointer?.experiment?.runId === id ? pointer.jobId ?? null : null
  })
  const mounted = useRef(false)
  const flight = useRef(false)
  const pending = useRef<Command | null>(null)
  const lossPosted = useRef(false)
  const stopRequested = useRef(false)
  const automatic = useRef<string | null>(null)
  const heading = useRef<HTMLElement>(null)

  const sync = useCallback(async (signal?: AbortSignal) => {
    const run = await getRun(id, signal)
    const receivedAt = performance.now()
    let ctx = contextRef.current
    if (!ctx) {
      const [plan, library] = await Promise.all([getPlan(run.plan_id, signal), listActivities(signal)])
      const activity = library.activities.find((item) => item.key === plan.activity_key)
      if (!activity || library.library_version !== plan.library_version || activity.library_version !== plan.library_version) {
        throw new ProtocolUnavailable()
      }
      ctx = { plan, activity }
      if (signal?.aborted || !mounted.current) return
      contextRef.current = ctx
      setContext(ctx)
    }
    if (signal?.aborted || !mounted.current) return
    ctl.dispatch({ type: 'experiment_sync', run, ...ctx, receivedAt })
  }, [ctl, id])

  useEffect(() => {
    mounted.current = true
    ctl.setUser(userId)
    const unsubscribe = ctl.subscribe((next) => {
      setState(next)
      setClock(performance.now())
      // A refused/unbuildable archive cannot reach onAccepted. Keep its
      // explanation visible and release Stop/Retry instead of waiting forever.
      if (next.phase === 'kept' && stopRequested.current) {
        stopRequested.current = false
        setSavingStop(false)
      }
    })
    ctl.start()
    const abort = new AbortController()
    void sync(abort.signal).catch((error: unknown) => { if (!abort.signal.aborted) setFailure(error) })
    const timer = setInterval(() => {
      if (!flight.current && ctl.state.phase !== 'recording') void sync(abort.signal).catch((error: unknown) => { if (!abort.signal.aborted) setFailure(error) })
    }, 2000)
    return () => { mounted.current = false; abort.abort(); clearInterval(timer); unsubscribe(); ctl.destroy() }
  }, [ctl, sync, userId])

  // Only identifiers and the original job key are replayed. Raw signal data is
  // never resumed or stitched after a refresh.
  useEffect(() => {
    const pointer = readPendingJob(userId)
    if (pointer?.experiment?.runId !== id) return
    if (pointer.jobId) return
    let cancelled = false
    void submitRecordingJob({ userId, uploadId: pointer.uploadId, condition: pointer.condition,
      deviceId: pointer.deviceId, experiment: pointer.experiment, idempotencyKey: pointer.jobKey })
      .then((job) => {
        if (cancelled) return
        writePendingJob(userId, { ...pointer, jobId: job.job_id })
        setOrphanJob(job.job_id)
        void sync().catch(setFailure)
      }).catch((error: unknown) => { if (!cancelled) setFailure(error) })
    return () => { cancelled = true }
  }, [id, sync, userId])

  const execute = useCallback(async (command: Command) => {
    if (flight.current) return
    flight.current = true
    pending.current = command
    setBusy(true)
    setFailure(null)
    try {
      await postEvent(id, command.body, command.key)
      // Mutation responses may omit server_time. A fresh read supplies the
      // authoritative clock and eligibility before any capture can start.
      await sync()
      pending.current = null
    } catch (error) {
      if (error instanceof ApiError && error.code === 'experiment.stale_revision') {
        pending.current = null
        await sync().catch(() => undefined)
      }
      if (mounted.current) setFailure(error)
    } finally {
      flight.current = false
      if (mounted.current) setBusy(false)
      if (command.body.kind === 'cancelled') {
        stopRequested.current = false
        if (mounted.current) setSavingStop(false)
      }
    }
  }, [id, sync])
  const event = useCallback((fields: Omit<ExperimentEvent, 'revision'>) => {
    const run = ctl.state.experiment?.run
    if (!run || flight.current) return
    void execute({ body: { ...fields, revision: run.revision }, key: crypto.randomUUID() })
  }, [ctl, execute])
  const finishStop = useCallback(async () => {
    try {
      await sync()
      const current = ctl.state.experiment?.run
      if (!current || ['cancelled', 'incomplete', 'complete'].includes(current.stage)) {
        stopRequested.current = false
        if (mounted.current) setSavingStop(false)
        return
      }
      await execute({ body: { kind: 'cancelled', revision: current.revision }, key: crypto.randomUUID() })
    } catch (error) {
      stopRequested.current = false
      if (mounted.current) { setSavingStop(false); setFailure(error) }
    }
  }, [ctl, execute, sync])
  const accepted = useCallback(() => {
    if (stopRequested.current) void finishStop()
    else void sync().catch(setFailure)
  }, [finishStop, sync])
  const stop = useCallback(() => {
    if (flight.current || stopRequested.current) return
    stopRequested.current = true
    setSavingStop(true)
    if (ctl.state.phase === 'recording') ctl.dispatch({ type: 'stop_early' })
    if (ctl.state.phase === 'journal') { ctl.journalDone(null); return }
    if (ctl.state.phase === 'processing' || ctl.state.phase === 'sending') {
      const pointer = readPendingJob(userId)
      if (pointer?.experiment?.runId !== id || !pointer.jobId) return
    }
    void finishStop()
  }, [ctl, finishStop, id, userId])

  const run = state.experiment?.run
  const experiment = selectExperiment(state, clock)
  const view = select(state, clock)
  const role = experiment?.role ?? null
  const captureLink = useMemo<ExperimentLink | undefined>(() => role ? { runId: id, role } : undefined, [id, role])
  const terminal = run?.stage === 'cancelled' || run?.stage === 'incomplete'
  const reportStage = run?.stage === 'awaiting_report' || run?.stage === 'complete'

  useEffect(() => {
    if (!run || failure || busy || stopRequested.current) return
    if (run.stage === 'activity_in_progress' && run.activity_started_at == null) {
      const token = JSON.stringify([run.id, run.revision, 'activity_started'])
      if (automatic.current === token) return
      automatic.current = token
      event({ kind: 'activity_started' })
    }
    if (run.stage === 'settling' && experiment?.settleRemainingS === 0) {
      const token = JSON.stringify([run.id, run.revision, 'settle_finished'])
      if (automatic.current === token) return
      automatic.current = token
      event({ kind: 'settle_finished' })
    }
  }, [busy, event, experiment?.settleRemainingS, failure, run])

  useEffect(() => {
    if (state.phase !== 'lost' || !role || lossPosted.current || terminal || state.reason === 'stopped' || state.experiment?.lossRevision == null) return
    lossPosted.current = true
    event({ kind: 'capture_lost', capture_role: role, loss_reason: ctl.captureLossReason })
  }, [ctl, event, role, state.experiment?.lossRevision, state.phase, state.reason, terminal])

  useEffect(() => {
    document.body.dataset.capturePhase = state.phase
    return () => { delete document.body.dataset.capturePhase }
  }, [state.phase])
  useEffect(() => { if (state.phase !== 'recording') heading.current?.focus() }, [run?.stage, state.phase])

  const checked = useCallback(() => { setFailure(null); void sync().catch(setFailure) }, [sync])
  const jobFinished = useCallback((job: Job) => {
    ctl.dispatch({ type: job.status === 'succeeded' ? 'scored' : 'unscored' })
    const pointer = readPendingJob(userId)
    if (pointer?.experiment?.runId === id && pointer.jobId === job.job_id) clearPendingJob(userId)
    setOrphanJob(null)
    void sync().catch(setFailure)
  }, [ctl, id, sync, userId])

  if (!run || !context || !experiment) return <div className="stack stack--standard">
    <PageHeader title={failure === null ? W.loading : W.sync} />
    {failure instanceof ProtocolUnavailable ? <Notice tone="warn"><p>{W.protocolUnavailable}</p><Button onClick={checked}>{W.sync}</Button></Notice>
      : failure !== null && <ProblemNotice error={failure} onRetry={checked}>{W.sync}</ProblemNotice>}
  </div>
  if (!report && reportStage && !savingStop) return <Navigate to={`/record/experiment/${encodeURIComponent(id)}/report`} replace />
  if (report && !reportStage) return <Navigate to={`/record/experiment/${encodeURIComponent(id)}`} replace />

  const lastAttempt = run.attempts?.filter((attempt) => attempt.capture_role === role).at(-1)
  const unusable = lastAttempt && ['lost', 'failed', 'rejected', 'empty'].includes(lastAttempt.status)
  const localCapture = ['recording', 'journal', 'processing', 'sending', 'scored', 'unscored', 'kept'].includes(state.phase)
  const lost = state.phase === 'lost' || (unusable && !retrying && !localCapture)
  const submittedJob = orphanJob ?? (lastAttempt?.status === 'submitted' ? lastAttempt.job_id : null)
  const stageTitle = report ? W.report : terminal ? (run.stage === 'cancelled' ? W.cancelled : W.incomplete)
    : state.phase === 'journal' ? W.journal
    : run.stage === 'activity_in_progress' ? W.activity : run.stage === 'settling' ? W.settle
      : role === 'after' ? W.readyAfter : W.readyBefore
  const canFinishActivity = context.activity.duration_s === null || experiment.activityRemainingS === 0 || run.deviations?.some((item) => item === 'shorter' || item === 'other')
  const canFinishSettle = experiment.settleRemainingS === 0
  const retry = () => { setRetrying(true); lossPosted.current = false; ctl.newAttempt() }

  return <div className="stack stack--standard capture experiment-flow" data-phase={state.phase}>
    <PageHeader ref={heading} tabIndex={-1} eyebrow={context.plan.custom_name ?? context.activity.name} title={stageTitle}
      back={<Link className="page__back" to="/record/experiment">← Choose an activity</Link>} />
    <ExperimentProtocol plan={context.plan} activity={context.activity} current={railStage[run.stage]} notes={false} />
    {context.activity.limit_text && <Notice tone="warn">
      {context.activity.exploratory && <Pill tone="amber" dot>{EXPERIMENT_WORDS.exploratory}</Pill>}
      <p>{context.activity.limit_text}</p>
    </Notice>}
    {failure !== null && <ProblemNotice error={failure} retryBusy={busy}
      onRetry={() => pending.current ? void execute(pending.current) : checked()}>{W.sync}</ProblemNotice>}
    {savingStop && <Notice><p>{W.savingStop}</p></Notice>}
    {savingStop && reportStage ? <Card><p>{W.savingStop}</p></Card> : report ? <Card><h2>{W.report}</h2><p>{W.terminalBody}</p>
      <dl className="detail-grid"><div className="detail-grid__item"><dt className="record__label">Before</dt><dd className="record__value record__value--mono product-id">{run.before_recording_id ?? '—'}</dd></div>
        <div className="detail-grid__item"><dt className="record__label">After</dt><dd className="record__value record__value--mono product-id">{run.after_recording_id ?? '—'}</dd></div></dl></Card>
      : terminal ? <Card><h2>{stageTitle}</h2><p>{W.terminalBody}</p></Card>
        : run.stage === 'activity_in_progress' ? <Card active>
          <h2>{context.plan.custom_name ?? context.activity.name}</h2>
          <div className="quiet__timer" role="timer" aria-label={context.activity.duration_s === null ? 'Activity elapsed time' : 'Activity time remaining'}>{context.activity.duration_s === null
            ? (experiment.activityElapsedS === null ? '—' : fmtTimer(experiment.activityElapsedS))
            : (experiment.activityRemainingS === null ? '—' : fmtTimer(experiment.activityRemainingS))}</div>
          {context.activity.duration_s === null && <p>{EXPERIMENT_WORDS.manualCompletion}</p>}
          <Button disabled={busy || savingStop || !run.activity_started_at || !canFinishActivity} onClick={() => event({ kind: 'activity_finished', headband_removed: experiment.headbandRemoved })}>{W.activityDone}</Button>
          <fieldset className="experiment-flow__deviation"><legend>{W.deviation}</legend>
            {([['shorter', W.shorter], ['longer', W.longer], ['other', W.other]] as const).map(([deviation, text]) =>
              <Button key={deviation} variant="secondary" disabled={busy || savingStop} onClick={() => event({ kind: 'deviation', deviation })}>{text}</Button>)}
          </fieldset>
          {run.deviations?.map((deviation, index) => <p key={`${index}:${deviation}`}>{deviation}</p>)}
        </Card> : run.stage === 'settling' ? <Card active><h2>{W.settle}</h2>
          <div className="quiet__timer" role="timer" aria-label="Settle time remaining">{experiment.settleRemainingS === null ? '—' : fmtTimer(experiment.settleRemainingS)}</div>
          {experiment.serverNow === null && <p>{W.noClock}</p>}
          <Button disabled={busy || savingStop || !canFinishSettle} onClick={() => event({ kind: 'settle_finished' })}>{W.settleDone}</Button>
        </Card> : <>
          {lost ? <Card><h2>{lastAttempt?.status === 'lost' || state.experiment?.lossRevision != null ? W.lost : W.unusable}</h2><p>{lastAttempt?.status === 'lost' || state.experiment?.lossRevision != null ? W.lostBody : W.unusableBody}</p>
            <Button disabled={busy || !experiment.canRetry} onClick={retry}>{W.retry}</Button>
            {!experiment.canRetry && <p>{W.terminalBody}</p>}
          </Card> : submittedJob ? <CaptureJob key={submittedJob} jobId={submittedJob} onTerminal={jobFinished} />
            : state.phase === 'off' ? <><p>{W.reconnect}</p><ConnectCard view={view} source={source} onSource={setSource}
              supported={webBluetoothAvailable()} onConnect={() => void ctl.connect(source)} /></>
              : state.phase === 'unsupported' ? <Notice tone="warn">{view.copy.lead}</Notice>
                : state.phase === 'scanning' ? <Card><p>{view.copy.title}</p></Card>
                  : ['fitting', 'stale', 'ready'].includes(state.phase) ? <>
                    <FittingScreen view={view} ctl={ctl} diagnostics />
                    <Card active><h2>{view.guidance?.title}</h2><p>{view.guidance?.body}</p>
                    <Button disabled={!view.canStart || !experiment.canStart || busy || savingStop} onClick={() => { lossPosted.current = false; setRetrying(false); ctl.startRecording() }}>{W.start}</Button>
                      {!experiment.canStart && <p>{experiment.serverNow === null ? W.noClock : W.windowClosed}</p>}
                    </Card></> : state.phase === 'recording' ? <Card active>
                      <div className="quiet"><div className="quiet__timer" role="timer" aria-label="Reading time remaining">{view.timer}</div><p>{W.eyesClosed}</p>
                        <ProgressBar label="Recording" value={view.progressPct} /></div>
                    </Card> : state.phase === 'journal' ? <CaptureJournal onDone={(payload) => ctl.journalDone(payload)} />
                      : state.phase === 'processing' ? <Card><p>{W.sending}</p></Card>
                        : state.phase === 'kept' ? <Card><h2>{W.unusable}</h2><p>{state.finishError}</p>
                          {state.report?.refusals.map((item) => <p key={item.code}>{item.message}</p>)}<Button onClick={retry}>{W.retry}</Button></Card>
                          : ctl.file && state.report && captureLink ? <SendRecording key={state.report.sessionId} file={ctl.file} deviceId={asDeviceId(state.report.layoutId)}
                            userId={userId} condition="EC" pair={undefined} experiment={captureLink} onAccepted={accepted} onTerminal={jobFinished} onAgain={checked} />
                            : <Card><p>{W.sync}</p><Button onClick={checked}>{W.sync}</Button></Card>}
        </>}
    {!terminal && !report && <div className="capture-actions"><Button variant="ghost" disabled={busy || savingStop} onClick={stop}>{W.stop}</Button></div>}
  </div>
}

function CaptureJob({ jobId, onTerminal }: { jobId: string; onTerminal: (job: Job) => void }) {
  const poll = useJobPolling(jobId)
  const reported = useRef(false)
  useEffect(() => {
    if (!poll.job || reported.current || !isTerminalJob(poll.job.status)) return
    reported.current = true
    onTerminal(poll.job)
  }, [onTerminal, poll.job])
  return poll.error ? <ProblemNotice error={poll.error} onRetry={poll.retry}>{W.sync}</ProblemNotice>
    : <JobWatch poll={poll} onStartOver={poll.retry} journal={false} revealResult={false} words={{ again: W.sync }} />
}
